/**
 * PATH: packages/email-hub/src/feature/components/sender-identity/predicates.ts
 * FILE_ID: EMAILHUB-SENDERIDENTITY-PREDICATES-001
 * PURPOSE: Pure gates deciding WHAT a sender is and WHICH filing verb applies.
 * ROLE IN ARCHITECTURE: Leaf of the sender-identity module; no React, no network.
 * MAIN EXPORTS: isSenderResolved, isTeammateSender, isResolvedSupplier,
 *   isSubscriptionSupplier, isSupplierSender, isRoleSender, isInternalSender,
 *   looksLikeRelaySender, shouldOfferAddToCrm, shouldOfferManualCrmFiling,
 *   shouldShowSenderIdentity, inferredVendorKind, VendorKind.
 * NON-RESPONSIBILITIES: No rendering, no mutations, no copy decisions.
 *
 * NOTES FOR FUTURE AI: Every gate here is a LANDMINE. Each one exists because a
 * specific wrong badge shipped and the operator reported it — the comments name
 * the report and the date. Widening a gate re-opens that exact complaint. Moved
 * verbatim out of SenderIdentityCard.tsx (change #302, size split); the only new
 * function is inferredVendorKind.
 */
import type { Email } from '../../hooks/useEmails.types'

/** How the operator wants to file an AP sender: a one-off trade ``supplier``
 *  or a recurring ``subscription`` (SaaS / membership / licence overhead).
 *  Threaded as the POST ``kind`` to create-sender-supplier. */
export type VendorKind = 'supplier' | 'subscription'

/** A sender is "known" once the CRM resolved EITHER a person (Contact)
 *  or a company (Client). Pure: maps the email's CRM fields → boolean. */
export function isSenderResolved(email: Pick<Email, 'contact_id' | 'client_id'>): boolean {
  return Boolean(email.contact_id || email.client_id)
}

/** Server-resolved TEAMMATE (internal, own-mail-domain) sender —
 *  BUG-2026-07-09-INTERNAL-SENDER-CRM. When true the identity slot must
 *  render the neutral Teammate chip and NEVER a customer-CRM badge,
 *  even if a stale Contact row resolved (Rebecca Newman rendered as
 *  "Known sender · John Smith"). Teammate identity beats CRM. Pure. */
export function isTeammateSender(
  email: Pick<Email, 'is_internal_sender'>,
): boolean {
  return email.is_internal_sender === true
}

/** Categories where the AI has read the mail as a genuine *external
 *  business interaction* — a quote / contract / invoice / supplier
 *  exchange — i.e. exactly the correspondence where filing the sender
 *  as a CRM contact is signal, not noise. The "Add to CRM" nudge is
 *  ONLY offered for these; everything else (vague 'general' /
 *  'uncategorised' mail, marketing blasts, and internal ops like
 *  timesheet / quality / safety) is left alone. Operator (2026-05-31):
 *  *"are you addicted to Add to CRM — it should only come when the AI
 *  picks up it's relevant, i.e. it's a quote request or something"*. */
const CRM_RELEVANT_CATEGORIES = new Set<string>([
  'quote_request', 'quote_submission', 'contract', 'variation',
  'invoice', 'payment', 'delivery', 'site_update',
  // NB: 'bill' and 'supplier' are deliberately EXCLUDED. Operator
  // (2026-06-02), on a rent/"Notice of breach" reminder from a property
  // manager: *"I should add that to not the CRM but Supplier… yet it said
  // add to your CRM"*. The CRM here is the CUSTOMER relationship database
  // (report.Client/Contact) — the people you quote + invoice. A
  // supplier/landlord is an AP relationship: it has no customer-CRM home,
  // and filing it as a Client would pollute the customer list. The
  // disposition the operator actually wants for AP-side mail is already
  // surfaced — "Send to Bills" (SmartActionBar primary for FINANCIAL_IN)
  // creates the bill + captures the vendor. So we don't nudge "Add to CRM"
  // for bill/supplier mail. ('invoice'/'payment' stay: an invoice from a
  // real individual is often an AR/customer contact — see the test.)
])

/** AP-side categories whose sender is a SUPPLIER/vendor, not a customer.
 *  These don't belong in the customer CRM (see CRM_RELEVANT_CATEGORIES),
 *  but the operator still wants the identity slot to NAME the relationship
 *  rather than nag "isn't in your CRM". Operator (2026-06-02), twice, on a
 *  rent reminder from a property manager: *"I should add that to not the
 *  CRM but Supplier"* → *"the 'isn't in your CRM' isn't fixed, it should be
 *  something else like Supplier"*. So for these we render a neutral
 *  "Supplier" identity badge (the file-the-supplier action is the adjacent
 *  Send to Bills verb; there is no separate supplier registry to add to). */
const SUPPLIER_CATEGORIES = new Set<string>(['supplier', 'bill'])

/** Has the sender RESOLVED to a known supplier in the master
 *  (quotebuilder.Supplier)? This is hard data from the backend
 *  (``supplier_id``), independent of the AI category — so it is
 *  definitive. Operator (2026-06-04): a Projex licence-agreement was
 *  tagged ``contract`` and so missed the category-only supplier check
 *  below, even though Projex is a known vendor. Pure. */
export function isResolvedSupplier(email: Pick<Email, 'supplier_id'>): boolean {
  return Boolean(email.supplier_id)
}

/** Did the operator file the matched vendor as a SUBSCRIPTION (a recurring
 *  SaaS/membership/licence overhead) rather than a one-off supplier? Reads the
 *  server's ``supplier_kind`` ('subscription'|'supplier'|null). Pure: a
 *  subscription is always also a supplier, so this only refines the BADGE the
 *  saved/resolved state renders (FEAT-2026-06-21-SUBSCRIPTION-VENDOR). */
export function isSubscriptionSupplier(email: Pick<Email, 'supplier_kind'>): boolean {
  return email.supplier_kind === 'subscription'
}

/** Which vendor kind does the system ALREADY believe this sender is?
 *
 *  The classifier learns a durable counterparty role (W3-V8) and the reader
 *  renders it as the "SaaS vendor" chip beside the address. Offering
 *  "Add as Supplier" and "Add as Subscription" as two equal buttons after
 *  that made the operator answer a question the system had already answered
 *  — on an Anthropic receipt, of all things (operator 2026-07-25: *"what the
 *  fuck why is it add as subscription?"*). One primary verb now follows the
 *  learned role; the other kind stays one click away, never removed.
 *
 *  Pure: (workflow_label.counterparty_role) → VendorKind. Defaults to
 *  'supplier', the safe direction — a trade supplier mis-filed as recurring
 *  overhead pollutes the Recurring spend view, whereas the reverse is just a
 *  missing subscription tag the operator can add.
 */
export function inferredVendorKind(
  email: Pick<Email, 'workflow_label'>,
): VendorKind {
  return email.workflow_label?.counterparty_role === 'saas_vendor'
    ? 'subscription'
    : 'supplier'
}

/** Is the sender a supplier/vendor (AP-side)? True when EITHER the
 *  sender resolved to a known supplier in the master (``supplier_id``)
 *  OR the AI category is an AP-side one (supplier/bill). The resolved
 *  signal wins regardless of category. Pure. */
export function isSupplierSender(
  email: Pick<Email, 'category' | 'supplier_id'>,
): boolean {
  if (isResolvedSupplier(email)) return true
  return SUPPLIER_CATEGORIES.has((email.category || '').toLowerCase())
}

/** Local-parts that are role / department / automated mailboxes — never a
 *  person you'd file as a CRM contact (strata@, accounts@, no-reply@,
 *  info@, …). Operator (2026-05-30): a strata-management admin email
 *  (strata@precise.property, category "General") was still being pushed at
 *  the CRM. The "Add to CRM" nudge is for individuals, not shared inboxes. */
const ROLE_LOCALPARTS = new Set<string>([
  'admin', 'administrator', 'info', 'hello', 'hi', 'contact', 'mail',
  'enquiries', 'enquiry', 'inquiries', 'inquiry', 'sales', 'support', 'help',
  'helpdesk', 'service', 'services', 'accounts', 'account', 'billing',
  'invoices', 'invoicing', 'payments', 'ar', 'ap', 'office', 'reception',
  'team', 'hr', 'humanresources', 'careers', 'jobs', 'recruitment',
  'marketing', 'newsletter', 'news', 'press', 'media', 'notifications',
  'notification', 'notice', 'notices', 'noreply', 'no-reply', 'donotreply',
  'do-not-reply', 'mailer', 'mailerdaemon', 'bounce', 'postmaster',
  'automated', 'alerts', 'alert', 'updates', 'update', 'statements',
  'statement', 'strata', 'property', 'properties', 'leasing', 'rentals',
  'rent', 'members', 'membership', 'subscriptions', 'subscribe',
  'unsubscribe', 'digest', 'feedback',
])

function localPartOf(addr: string | null | undefined): string {
  return (addr || '').split('@')[0].toLowerCase().trim()
}

/** A role / department / automated mailbox rather than an individual. */
export function isRoleSender(addr: string | null | undefined): boolean {
  const local = localPartOf(addr)
  if (!local) return false
  if (/no-?reply|do-?not-?reply|mailer-?daemon/.test(local)) return true
  // accounts2 / info-au / billing.us → strip a region/team suffix + digits
  const base = local
    .replace(/[._-]?(au|uk|us|nz|global|team)$/i, '')
    .replace(/\d+$/, '')
  return ROLE_LOCALPARTS.has(local) || ROLE_LOCALPARTS.has(base)
}

/** Display name that reads as an organisation / bulk sender rather than a
 *  person — a brand separator ("Strata | Precise Property") or a company /
 *  bulk keyword ("HDFC Bank InstaAlerts", "… E-Newsletter"). */
const ORG_NAME_RE =
  /[|·•]|\b(pty|ltd|limited|inc\.?|llc|gmbh|co\.?|group|holdings|management|property|properties|strata|bank|insurance|capital|partners|realty|real estate|newsletter|e-?news|enews|alerts?|notifications?|team|noreply|no-reply|updates?)\b/i

function looksLikeOrgSender(name: string | null | undefined): boolean {
  const n = (name || '').trim()
  if (!n) return false
  return ORG_NAME_RE.test(n)
}

/** Automated RELAY / e-signature / accounting platforms that send mail
 *  ON BEHALF OF someone else — DocuSign, Adobe Sign, Xero, etc. The
 *  envelope sender is the platform's robot mailbox (dse@*.docusign.net),
 *  never a person you'd file as a CRM contact, and the underlying mail is
 *  usually a finance/legal artefact (a retainer, a bill) that belongs to a
 *  Chart-of-Accounts line, not the customer CRM. Operator (2026-06-07), on
 *  a DocuSign legal-retainer wrongly nagging "Add to CRM": *"what the fuck
 *  do you mean CRM this is absolutely under CoA lawyer"*. Mirrors the
 *  backend relay-domain guard in the email→job adjudicator. */
const RELAY_DOMAIN_RE =
  /(?:^|\.)(?:docusign\.(?:net|com)|post\.xero\.com|(?:mail|messaging)\.xero\.com|adobesign\.com|echosign\.com|hellosign\.com|dropboxsign\.com|pandadoc\.com|signnow\.com|eversign\.com|docusignmail\.com|dochub\.com|ondmarc\.com)$/i

/** Display names that read as "<person> via <e-sign/accounting platform>"
 *  — DocuSign/Adobe Sign/Xero stamp the original signer's name then "via
 *  <brand>". Catches relays whose envelope domain we don't enumerate. */
const VIA_RELAY_NAME_RE =
  /\bvia\s+(docusign|adobe\s?sign|echosign|hellosign|dropbox\s?sign|pandadoc|sign\s?now|eversign|xero)\b/i

function domainOf(addr: string | null | undefined): string {
  const at = (addr || '').split('@')
  return at.length > 1 ? at[at.length - 1].toLowerCase().trim() : ''
}

/** Is the SENDER one of our own people (an internal colleague/employee)
 *  rather than an external party? True when the sender's domain matches
 *  the domain of the org mailbox that RECEIVED this message
 *  (``synced_by_mailbox_email`` / ``synced_by_email``). Operator
 *  (2026-06-10), on ``mitchell.brood@newmark.com.au`` — an internal
 *  estimator — being nagged "Add to CRM": he's staff, not a customer
 *  lead. The CRM is the *customer* relationship database; a colleague has
 *  no home there. Conservative: only fires when BOTH domains are known
 *  and equal, so it can never wrongly hide a genuine external lead (it can
 *  at worst miss an internal sender on a second org domain — safe
 *  direction). Pure: (from, receiving-mailbox) → boolean.
 *
 *  LANDMINE (change #302): this reads the same receiving-mailbox fields the
 *  reader's "via <mailbox>" line does. Those were absent from the DETAIL
 *  payload for a whole release, so on an opened email this gate silently
 *  degraded to "no org domain known → not internal". It now resolves on
 *  both payloads; keep them on both. */
export function isInternalSender(
  email: Pick<
    Email,
    'from_address' | 'synced_by_mailbox_email' | 'synced_by_email' | 'is_internal_sender'
  >,
): boolean {
  // The server's verdict wins over the local same-domain heuristic when
  // present: `false` means the operator pinned this sender as NOT a
  // teammate ("Not a teammate" chip action, 2026-07-09), so the normal
  // CRM/supplier affordances must come back.
  if (email.is_internal_sender === false) return false
  const senderDomain = domainOf(email.from_address)
  if (!senderDomain) return false
  const orgDomain = domainOf(email.synced_by_mailbox_email) || domainOf(email.synced_by_email)
  return Boolean(orgDomain) && senderDomain === orgDomain
}

/** Is this an automated relay / e-sign / accounting-platform sender?
 *  True when EITHER the envelope domain is a known relay OR the display
 *  name reads "… via <e-sign brand>". Pure: (address, name) → boolean. */
export function looksLikeRelaySender(
  email: Pick<Email, 'from_address' | 'from_name'>,
): boolean {
  const domain = domainOf(email.from_address)
  if (domain && RELAY_DOMAIN_RE.test(domain)) return true
  return VIA_RELAY_NAME_RE.test((email.from_name || '').trim())
}

/** Should we PROMPT to add an UNKNOWN sender to the CRM? Allow-list, not
 *  deny-list: we only nudge when the AI has classified the mail as a
 *  genuine external business interaction (a quote / contract / invoice /
 *  supplier exchange — see ``CRM_RELEVANT_CATEGORIES``). Vague
 *  'general' / 'uncategorised' mail and internal ops never trigger the
 *  prompt. Even within the relevant categories we still skip
 *  role/admin/automated mailboxes (strata@, accounts@, no-reply@) and
 *  org-branded bulk display names. Known senders always show the green
 *  badge regardless — this gate is purely about the "Add to CRM" nudge. */
export function shouldOfferAddToCrm(
  email: Pick<
    Email,
    | 'category'
    | 'from_address'
    | 'from_name'
    | 'supplier_id'
    | 'synced_by_mailbox_email'
    | 'synced_by_email'
  >,
): boolean {
  // An INTERNAL sender (a colleague on our own mail domain) is never a
  // customer CRM lead — suppress the nudge outright, whatever the AI
  // category (operator 2026-06-10: an internal estimator was nagged "Add
  // to CRM"). See ``isInternalSender``.
  if (isInternalSender(email)) return false
  // A KNOWN supplier is never a CRM lead — suppress the nudge outright,
  // whatever the AI category (operator 2026-06-04: Projex supplier
  // licence-agreement tagged ``contract`` was wrongly offered "Add to
  // CRM"). The supplier badge renders instead.
  if (isResolvedSupplier(email)) return false
  // Automated relay / e-sign / accounting platforms (DocuSign, Adobe Sign,
  // Xero) are never a CRM lead — the sender is a robot mailbox and the mail
  // is a finance/legal artefact, not a customer relationship (operator
  // 2026-06-07: a DocuSign legal retainer, tagged ``contract``, wrongly
  // nagged "Add to CRM"). Suppress regardless of category.
  if (looksLikeRelaySender(email)) return false
  if (!CRM_RELEVANT_CATEGORIES.has((email.category || '').toLowerCase())) {
    return false
  }
  if (isRoleSender(email.from_address)) return false
  if (looksLikeOrgSender(email.from_name)) return false
  return true
}

/** Should the reader offer a MANUAL "file this sender" affordance in the
 *  identity slot for mail the AI did NOT auto-classify as a lead/supplier?
 *
 *  This is the gate for the small multi-type Add-to-CRM chip that replaces
 *  the old dead "Not in CRM" label (operator 2026-06-16: *"what do you mean
 *  not in CRM… CRM etc are categories… which should self-populate if
 *  anything"*). Unlike ``shouldOfferAddToCrm`` (the AI NUDGE, gated to
 *  business categories) this is a passive, manual tool — so it's allowed for
 *  uncategorised mail too. We still suppress it where filing is meaningless,
 *  so it never re-becomes the "addicted to Add to CRM" nag the operator
 *  pushed back on:
 *    - already resolved (it's in the CRM — the green badge shows instead)
 *    - an internal colleague (no customer-CRM home)
 *    - an automated relay / e-sign robot (DocuSign/Xero — not a person)
 *    - a role / no-reply / automated mailbox (accounts@, news-noreply@)
 *  Everything else (a real-looking person OR a company sender) is fileable
 *  as a Contact/Client OR a Supplier. Pure: email fields → boolean. */
export function shouldOfferManualCrmFiling(
  email: Pick<
    Email,
    | 'contact_id'
    | 'client_id'
    | 'from_address'
    | 'from_name'
    | 'supplier_id'
    | 'synced_by_mailbox_email'
    | 'synced_by_email'
  >,
): boolean {
  if (isSenderResolved(email)) return false
  if (isInternalSender(email)) return false
  if (looksLikeRelaySender(email)) return false
  if (isRoleSender(email.from_address)) return false
  return true
}

/** Whether the identity strip renders at all. Known senders always show
 *  the green badge; unknown senders only render (with the "Add to CRM"
 *  nudge) when the mail is individual relationship mail, so the host can
 *  skip the wrapping margin entirely. */
export function shouldShowSenderIdentity(
  email: Pick<
    Email,
    | 'contact_id'
    | 'client_id'
    | 'category'
    | 'from_address'
    | 'from_name'
    | 'supplier_id'
    | 'synced_by_mailbox_email'
    | 'synced_by_email'
    | 'is_internal_sender'
  >,
): boolean {
  // Server-resolved teammate: always render the neutral internal badge
  // (it also SUPPRESSES any stale CRM match — see the component).
  if (isTeammateSender(email)) return true
  if (isSenderResolved(email)) return true
  // Internal colleague mail: no CRM/supplier identity strip at all (it's
  // neither a customer lead nor an AP vendor). Only the green "known
  // sender" badge above survives, for the rare case an employee also
  // happens to be a resolved contact.
  if (isInternalSender(email)) return false
  if (shouldOfferAddToCrm(email)) return true
  // Supplier/vendor mail: render the "Supplier" identity badge instead of
  // the (wrong) "Add to CRM" nudge or a bare "Not in CRM" fallback.
  return isSupplierSender(email)
}
